import type { IStyleParam } from '@wix/tpa-settings';
import {
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';

const defaultBodyMFont: NonNullable<
  IStyleParam<StyleParamType.Font>['getDefaultValue']
> = (params) =>
  wixFontParam('Body-M', {
    htmlTag: 'p',
    size: params.isMobile ? 14 : 16,
  })(params);

export type FulfillmentPickerStylesParams = {
  regularFont: StyleParamType.Font;
  regularTextColor: StyleParamType.Color;
  regularBackgroundColor: StyleParamType.Color;
  regularBorderColor: StyleParamType.Color;
  regularBorderWidth: StyleParamType.Number;
  regularCornerRadiusTopLeft: StyleParamType.Number;
  regularCornerRadiusTopRight: StyleParamType.Number;
  regularCornerRadiusBottomLeft: StyleParamType.Number;
  regularCornerRadiusBottomRight: StyleParamType.Number;
  regularCornerRadiusLinked: StyleParamType.Number;

  selectedFont: StyleParamType.Font;
  selectedTextColor: StyleParamType.Color;
  selectedBackgroundColor: StyleParamType.Color;
  selectedBorderColor: StyleParamType.Color;
  selectedBorderWidth: StyleParamType.Number;
  selectedCornerRadiusTopLeft: StyleParamType.Number;
  selectedCornerRadiusTopRight: StyleParamType.Number;
  selectedCornerRadiusBottomLeft: StyleParamType.Number;
  selectedCornerRadiusBottomRight: StyleParamType.Number;
  selectedCornerRadiusLinked: StyleParamType.Number;
};

export const fulfillmentPickerStylesParams =
  createStylesParams<FulfillmentPickerStylesParams>({
    regularFont: {
      type: StyleParamType.Font,
      getDefaultValue: defaultBodyMFont,
      metadata: {
        type: StyleType.font,
        defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
        mobileDefaultValue: { type: 'static', preset: 'Body-M', size: 14 },
        ai: {
          description:
            'Font for an unselected fulfillment method tab (the Delivery/Pickup selector at the top of the ordering flow)',
        },
      },
    },
    regularTextColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.color,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description:
            'Text color for an unselected fulfillment method tab (Delivery/Pickup selector)',
        },
      },
    },
    regularBackgroundColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-1', 1),
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: { type: 'static', preset: 'color-1', opacity: 1 },
        ai: {
          description:
            'Background color for an unselected fulfillment method tab (Delivery/Pickup selector)',
        },
      },
    },
    regularBorderColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.borderColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description:
            'Border color for an unselected fulfillment method tab (Delivery/Pickup selector)',
        },
      },
    },
    regularBorderWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.borderWidth,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            'Border thickness (px) for an unselected fulfillment method tab',
        },
      },
    },
    regularCornerRadiusTopLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top-left corner radius (px) of an unselected fulfillment method tab',
        },
      },
    },
    regularCornerRadiusTopRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top-right corner radius (px) of an unselected fulfillment method tab',
        },
      },
    },
    regularCornerRadiusBottomLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom-left corner radius (px) of an unselected fulfillment method tab',
        },
      },
    },
    regularCornerRadiusBottomRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom-right corner radius (px) of an unselected fulfillment method tab',
        },
      },
    },
    regularCornerRadiusLinked: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            '1 = all corner radii of the unselected fulfillment tab locked together, 0 = each corner independent',
        },
      },
    },

    selectedFont: {
      type: StyleParamType.Font,
      getDefaultValue: defaultBodyMFont,
      metadata: {
        type: StyleType.font,
        defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
        mobileDefaultValue: { type: 'static', preset: 'Body-M', size: 14 },
        ai: {
          description:
            'Font for the active fulfillment method tab (the currently chosen Delivery or Pickup option)',
        },
      },
    },
    selectedTextColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-8', 1),
      metadata: {
        type: StyleType.color,
        defaultValue: { type: 'static', preset: 'color-8', opacity: 1 },
        ai: {
          description:
            'Text color for the active fulfillment method tab (Delivery/Pickup selector)',
        },
      },
    },
    selectedBackgroundColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-8', 0.1),
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: { type: 'static', preset: 'color-8', opacity: 0.1 },
        ai: {
          description:
            'Background color for the active fulfillment method tab (Delivery/Pickup selector)',
        },
      },
    },
    selectedBorderColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-8', 1),
      metadata: {
        type: StyleType.borderColor,
        defaultValue: { type: 'static', preset: 'color-8', opacity: 1 },
        ai: {
          description:
            'Border color for the active fulfillment method tab (Delivery/Pickup selector)',
        },
      },
    },
    selectedBorderWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.borderWidth,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            'Border thickness (px) for the active fulfillment method tab',
        },
      },
    },
    selectedCornerRadiusTopLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top-left corner radius (px) of the active fulfillment method tab',
        },
      },
    },
    selectedCornerRadiusTopRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top-right corner radius (px) of the active fulfillment method tab',
        },
      },
    },
    selectedCornerRadiusBottomLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom-left corner radius (px) of the active fulfillment method tab',
        },
      },
    },
    selectedCornerRadiusBottomRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom-right corner radius (px) of the active fulfillment method tab',
        },
      },
    },
    selectedCornerRadiusLinked: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            '1 = all corner radii of the active fulfillment tab locked together, 0 = each corner independent',
        },
      },
    },
  });
